// Selected Journey run detail. The timeline directly renders durable step/assertion/evidence rows; it
// recomputes no verdict and links no isolated-run object into a main-database screen. Execution mode always
// comes from its explicit discriminant, never from optional provenance.

import { Icon } from '../../app/Icon.tsx';
import type {
  JourneyAssertionView,
  JourneyEvidenceView,
  JourneyRunView,
  JourneyStepView,
} from '../journey-lab-api.ts';
import {
  costLabel,
  journeyStamp,
  journeyStatusLabel,
  journeyStepStatusLabel,
  journeyStepTone,
  journeyTone,
  shortDefinitionHash,
} from '../journey-lab-view.ts';
import { journeyRunModePresentation, type JourneyModePresentation } from '../journey-language.ts';
import { Btn, EmptyState, Panel, SectionHead, StatusPill } from '../primitives.tsx';

function ModeEvidence({ run }: { readonly run: JourneyRunView }): React.JSX.Element {
  const presentation = journeyRunModePresentation(run);
  return (
    <Panel
      className="mt-4 bg-surface-sunken p-4"
      dataMarker={{ name: 'execution-mode', value: presentation.marker }}
    >
      <div className="text-sm font-semibold text-text">{presentation.label}</div>
      <p className="mt-1 text-sm text-text-muted">{presentation.proofBoundary}</p>
    </Panel>
  );
}

function providerModeFact(presentation: JourneyModePresentation, providerValue: string): string {
  if (presentation.key === 'provider_live') return providerValue;
  return presentation.externalServices === 'not_used' ? 'Not applicable' : 'Not proven';
}

function RunFacts({ run }: { readonly run: JourneyRunView }): React.JSX.Element {
  const presentation = journeyRunModePresentation(run);
  const isProviderMode = presentation.key === 'provider_live';
  const model = isProviderMode
    ? liveModelLabel(run)
    : presentation.externalServices === 'not_used'
      ? 'Not invoked'
      : 'Not proven';
  const tokens = providerModeFact(presentation, String(run.tokens ?? 'Not recorded'));
  const cost = providerModeFact(presentation, costLabel(run.costPence));
  return (
    <dl className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
      <div>
        <dt className="text-xs text-text-subtle">Execution mode</dt>
        <dd className="mt-1 text-text-muted">{presentation.label}</dd>
      </div>
      <div>
        <dt className="text-xs text-text-subtle">Model</dt>
        <dd className="mt-1 text-text-muted">{model}</dd>
      </div>
      <div>
        <dt className="text-xs text-text-subtle">Definition</dt>
        <dd className="ritona-numeric mt-1 text-text-muted" title={run.definitionHash}>
          {shortDefinitionHash(run.definitionHash)}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-text-subtle">Requested</dt>
        <dd className="mt-1 text-text-muted">
          <time dateTime={run.requestedAt}>{journeyStamp(run.requestedAt)}</time>
        </dd>
      </div>
      <div>
        <dt className="text-xs text-text-subtle">Tokens</dt>
        <dd className="ritona-numeric mt-1 text-text-muted">{tokens}</dd>
      </div>
      <div>
        <dt className="text-xs text-text-subtle">Cost</dt>
        <dd className="ritona-numeric mt-1 text-text-muted">{cost}</dd>
      </div>
    </dl>
  );
}

function liveModelLabel(run: JourneyRunView): string {
  const identity = [run.provider, run.model]
    .filter((part): part is string => part !== undefined)
    .join(' · ');
  if (identity === '') return 'Not recorded';
  const posture = {
    verified: 'Provider-corroborated',
    requested_not_corroborated: 'Requested · not provider-corroborated',
    mismatch: 'Mismatch detected',
    missing: 'Requested · observation missing',
  }[run.modelVerification ?? 'missing'];
  return `${identity} · ${posture}`;
}

function StepHeader({ step }: { readonly step: JourneyStepView }): React.JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <div
        className="ritona-numeric flex size-8 shrink-0 items-center justify-center rounded-sm border border-border text-xs text-text-muted"
        aria-hidden="true"
      >
        {step.sequence}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="ritona-numeric text-xs text-text-subtle">
              {step.stepId} · {step.actor} → {step.action}
            </div>
            <h3 id={`journey-step-${step.stepId}`} className="mt-1 font-semibold text-text">
              {step.beat}
            </h3>
          </div>
          <StatusPill tone={journeyStepTone(step.state, step.verdict)}>
            {journeyStepStatusLabel(step.state, step.verdict)}
          </StatusPill>
        </div>
        {step.detail !== undefined ? (
          <p className="mt-2 text-sm text-text-muted">{step.detail}</p>
        ) : null}
      </div>
    </div>
  );
}

function AssertionRow({
  assertion,
}: {
  readonly assertion: JourneyAssertionView;
}): React.JSX.Element {
  return (
    <li
      className="flex items-start gap-2 text-sm"
      data-assertion={assertion.kind}
      data-assertion-pass={String(assertion.pass)}
    >
      <span className={assertion.pass ? 'text-forest' : 'text-clay'}>
        <Icon name={assertion.pass ? 'check' : 'ban'} size={16} />
      </span>
      <span>
        <span className="font-medium text-text">{assertion.kind}</span>
        <span className="mt-1 block text-text-muted">{assertion.detail}</span>
      </span>
    </li>
  );
}

function AssertionList({ step }: { readonly step: JourneyStepView }): React.JSX.Element {
  return (
    <section aria-label={`Assertions for ${step.stepId}`}>
      <h4 className="ritona-numeric text-xs uppercase tracking-wide text-text-subtle">
        Assertions
      </h4>
      {step.assertions.length === 0 ? (
        <p className="mt-2 text-sm text-text-subtle">No assertions recorded.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {step.assertions.map((assertion, index) => (
            <AssertionRow key={`${assertion.kind}-${String(index)}`} assertion={assertion} />
          ))}
        </ul>
      )}
    </section>
  );
}

function EvidenceRow({ evidence }: { readonly evidence: JourneyEvidenceView }): React.JSX.Element {
  return (
    <li className="border-l border-border pl-3 text-sm" data-evidence={evidence.kind}>
      <span className="font-medium text-text">{evidence.kind}</span>
      <span className="mt-1 block text-text-muted">{evidence.summary}</span>
      {evidence.ref !== undefined ? (
        <span className="ritona-numeric mt-1 block text-xs text-text-subtle">
          Reference {evidence.ref}
        </span>
      ) : null}
    </li>
  );
}

function EvidenceList({ step }: { readonly step: JourneyStepView }): React.JSX.Element {
  return (
    <section aria-label={`Evidence for ${step.stepId}`}>
      <h4 className="ritona-numeric text-xs uppercase tracking-wide text-text-subtle">Evidence</h4>
      {step.evidence.length === 0 ? (
        <p className="mt-2 text-sm text-text-subtle">No evidence recorded.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {step.evidence.map((evidence, index) => (
            <EvidenceRow key={`${evidence.kind}-${String(index)}`} evidence={evidence} />
          ))}
        </ul>
      )}
    </section>
  );
}

function StepTimelineItem({ step }: { readonly step: JourneyStepView }): React.JSX.Element {
  return (
    <li
      className="panel p-4"
      data-journey-step={step.stepId}
      data-step-state={step.state}
      data-step-verdict={step.verdict}
    >
      <article aria-labelledby={`journey-step-${step.stepId}`}>
        <StepHeader step={step} />
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <AssertionList step={step} />
          <EvidenceList step={step} />
        </div>
      </article>
    </li>
  );
}

interface JourneyRunDetailProps {
  readonly run: JourneyRunView;
  readonly replaying: boolean;
  readonly onReplay: (scenarioId: string) => void;
}

function RunSummary({ run, replaying, onReplay }: JourneyRunDetailProps): React.JSX.Element {
  return (
    <Panel className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-4" aria-live="polite">
        <div>
          <div className="ritona-numeric text-xs uppercase tracking-wide text-forest">
            {run.scenarioId}
          </div>
          <h2 className="mt-1 text-lg font-semibold text-text">{run.title}</h2>
          <div className="ritona-numeric mt-1 text-xs text-text-subtle">Run {run.runId}</div>
        </div>
        <StatusPill tone={journeyTone(run.state, run.verdict)}>
          {journeyStatusLabel(run.state, run.verdict)}
        </StatusPill>
      </div>
      <RunFacts run={run} />
      {run.interruptionDetail !== undefined ? (
        <p role="status" className="mt-4 border-l-2 border-clay pl-3 text-sm text-text-muted">
          Interrupted: {run.interruptionDetail}
        </p>
      ) : null}
      <ModeEvidence run={run} />
      <div className="mt-4">
        {run.executionMode === 'scripted' ? (
          <Btn
            kind="ghost"
            sm
            disabled={replaying}
            ariaLabel={`Run ${run.title} as a scripted check again`}
            onClick={() => onReplay(run.scenarioId)}
          >
            {replaying ? 'Queueing…' : 'Run scripted check again'}
          </Btn>
        ) : (
          <p className="text-sm text-text-subtle">
            {journeyRunModePresentation(run).key === 'unrecorded'
              ? 'This run’s execution mode is not recorded; start another check only from the catalogue after a fresh preflight.'
              : 'A full-flow check can only be started again from the catalogue after a fresh preflight and arm.'}
          </p>
        )}
      </div>
    </Panel>
  );
}

function RunTimeline({ run }: { readonly run: JourneyRunView }): React.JSX.Element {
  return (
    <div className="mt-6">
      <SectionHead label="Step, assertion & evidence timeline" />
      {run.steps.length === 0 ? (
        <EmptyState>
          {run.state === 'queued'
            ? 'This run is queued. Steps will appear when execution starts.'
            : 'No step evidence has been recorded for this run.'}
        </EmptyState>
      ) : (
        <ol aria-label="Run timeline" className="space-y-3">
          {run.steps.map((step) => (
            <StepTimelineItem key={step.stepId} step={step} />
          ))}
        </ol>
      )}
    </div>
  );
}

export function JourneyRunDetail(props: JourneyRunDetailProps): React.JSX.Element {
  return (
    <section aria-label="Selected journey run" data-journey-run={props.run.runId}>
      <RunSummary {...props} />
      <RunTimeline run={props.run} />
    </section>
  );
}
